import React, { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { COUNTRY_CODES, flagFromIso, splitPhone, composePhone } from "@/lib/countryCodes";

// WhatsApp / phone input with a country-code dropdown.
//
// The parent stores a single full E.164-style string (e.g. "+6281234"). This
// component splits it into `{ dial, local }` on render and re-composes it on
// every keystroke via `onChange`. The operator only ever types the local
// digits — the dial code is chosen from a searchable list of countries.
//
// Props:
//   value       — full phone string (or "" for a new record)
//   onChange    — receives the newly-composed full phone string
//   placeholder — placeholder for the local-digit input (default "812 3456 7890")
//   testId      — data-testid prefix (default "phone-input")
//   defaultIso  — country to preselect when `value` is blank (default "ID")
//   disabled    — disables both the dropdown and the local input
export default function PhoneInput({
  value,
  onChange,
  placeholder = "812 3456 7890",
  testId = "phone-input",
  defaultIso = "ID",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  // Iter-294 · CRITICAL FIX: sticky ISO for the user's explicit pick.
  // Without this, selecting Bahrain (+973) with an empty local number
  // caused `composePhone` to emit "" → the useMemo below re-derived
  // the flag from an empty `value` and reset back to `defaultIso`
  // (Indonesia / +62). The country picker appeared "stuck" on +62.
  // The sticky state survives value="" transitions and honours the
  // operator's choice until they explicitly pick another country
  // (or the parent injects a fully-formed number from the DB).
  const [pickedIso, setPickedIso] = useState(null);

  // Derive the current dial + local pair from the parent's `value`. When the
  // caller passes "" we still surface a dial code — either the sticky pick
  // or the default country — so the operator sees which prefix will be
  // applied when they start typing.
  const { dial, local, iso } = useMemo(() => {
    if (!value) {
      const isoKey = pickedIso || defaultIso;
      const fallback = COUNTRY_CODES.find((c) => c.iso === isoKey) || COUNTRY_CODES[0];
      return { dial: fallback.dial, local: "", iso: fallback.iso };
    }
    const s = splitPhone(value);
    // splitPhone gives us the dial + local pair; recover the ISO by matching
    // both dial code AND the raw digits (so "+1..." resolves to US, not CA).
    const digits = value.replace(/^\+/, "").replace(/\D/g, "");
    const candidates = COUNTRY_CODES.filter((c) => c.dial === s.dial && digits.startsWith(c.dial));
    let match = candidates[0];
    if (candidates.length > 1) {
      // Prefer the operator's explicit pick when the dial code is shared
      // (US / CA / JM / PR share +1). Otherwise fall back to US, then
      // defaultIso, then the first candidate.
      match = (pickedIso && candidates.find((c) => c.iso === pickedIso)) ||
              candidates.find((c) => c.iso === "US") ||
              candidates.find((c) => c.iso === defaultIso) ||
              candidates[0];
    }
    return { dial: s.dial, local: s.local, iso: match?.iso || defaultIso };
  }, [value, defaultIso, pickedIso]);

  const selectCountry = (nextIso) => {
    const c = COUNTRY_CODES.find((x) => x.iso === nextIso);
    if (!c) return;
    setPickedIso(nextIso);
    onChange(composePhone(c.dial, local));
    setOpen(false);
  };

  const handleLocal = (raw) => {
    // Strip any non-digit characters the operator pastes/types (spaces, dashes,
    // parentheses) — the stored form is always digits-only after "+<dial>".
    const clean = raw.replace(/\D/g, "");
    onChange(composePhone(dial, clean));
  };

  return (
    <div className="flex gap-2" data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "flex h-10 items-center gap-1 rounded-md border border-input bg-background px-2.5 text-sm ring-offset-background",
              "hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50 flex-shrink-0",
            )}
            data-testid={`${testId}-code-trigger`}
          >
            <span className="text-base leading-none" aria-hidden="true">{flagFromIso(iso)}</span>
            <span className="text-muted-foreground">+{dial}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[280px]" align="start">
          <Command
            filter={(cmdValue, search) => {
              // cmdValue is "<iso>|<name>|<dial>" — match against any part.
              const q = search.toLowerCase();
              return cmdValue.toLowerCase().includes(q) ? 1 : 0;
            }}
          >
            <CommandInput placeholder="Search country or code…" data-testid={`${testId}-code-search`} />
            <CommandList>
              <CommandEmpty>No match.</CommandEmpty>
              {COUNTRY_CODES.map((c) => (
                <CommandItem
                  key={c.iso}
                  value={`${c.iso}|${c.name}|${c.dial}`}
                  onSelect={() => selectCountry(c.iso)}
                  data-testid={`${testId}-code-item-${c.iso}`}
                >
                  <Check className={cn("mr-2 h-4 w-4", iso === c.iso ? "opacity-100" : "opacity-0")} />
                  <span className="mr-2" aria-hidden="true">{flagFromIso(c.iso)}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground text-xs">+{c.dial}</span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={local}
        onChange={(e) => handleLocal(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={`${testId}-local`}
        className="flex-1"
      />
    </div>
  );
}
